import { Badge, Button } from '@cloudflare/kumo';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ICONS } from '../ui/icons';
import { jevEvents, askJev, closeJev, type JevOpen } from './jevEvents';
import { JevComposer } from './JevComposer';
import { JevConversation } from './JevConversation';
import { useJevChat } from './useJevChat';
import '../../styles/jev.css';

const SUGGESTIONS = ['Quantas conversas a IA resolveu esta semana?', 'Quantos contatos têm a tag vip?', 'Quais negócios fecham este mês?'];

/** Botão "Perguntar à Lumi" + painel lateral (tela cheia no celular); também abre pela busca global (Ctrl+K) */
export function JevPanel() {
  const chat = useJevChat();
  const { page, items, threads, threadId, send, newChat, loadThreads, loadThread, setInput, inputRef } = chat;
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'chat' | 'history'>('chat');
  // Pedidos vindos de fora do painel (tela Perguntar à IA): pergunta para enviar já ou conversa para abrir
  const [autoSend, setAutoSend] = useState<string | null>(null);
  const [openThread, setOpenThread] = useState<string | null>(null);

  // Pergunta da tela Perguntar à IA: sempre numa conversa nova
  useEffect(() => {
    if (!autoSend || chat.busy) return;
    const q = autoSend;
    setAutoSend(null);
    send(q, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSend]);

  // Conversa recente escolhida na tela Perguntar à IA
  useEffect(() => {
    if (!openThread) return;
    const id = openThread;
    setOpenThread(null);
    loadThread(id).then(() => setView('chat'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openThread]);

  // Esc fecha; o Ctrl+K agora é da busca global, que oferece "Perguntar à Lumi" (askJev)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) closeJev();
    };
    const onAsk = (e: Event) => {
      setOpen(true); setView('chat');
      const d = (e as CustomEvent<JevOpen | string>).detail;
      const o: JevOpen = typeof d === 'string' ? { text: d } : d ?? { text: '' };
      if (o.threadId) setOpenThread(o.threadId);
      else if (o.send && o.text.trim()) setAutoSend(o.text);
      else if (o.text) setInput(o.text);
      setTimeout(() => inputRef.current?.focus(), 0);
    };
    window.addEventListener('keydown', onKey);
    jevEvents.addEventListener('open', onAsk);
    return () => { window.removeEventListener('keydown', onKey); jevEvents.removeEventListener('open', onAsk); };
  }, [open, setInput, inputRef]);

  // Sincroniza o estado interno com o 'close' vindo de fora (ex.: clique no overlay
  // da busca global, navegação que fecha o painel)
  useEffect(() => {
    const onClose = () => setOpen(false);
    jevEvents.addEventListener('close', onClose);
    return () => jevEvents.removeEventListener('close', onClose);
  }, []);

  const openHistory = async () => { setView('history'); await loadThreads(); };
  const pick = async (id: string) => { await loadThread(id); setView('chat'); };

  return (
    <>
      <button type="button" className="jev-trigger" onClick={() => {
        if (open) {
          closeJev();
        } else {
          askJev();
        }
      }} aria-expanded={open} aria-controls="jev-panel" title="Perguntar à Lumi">
        <ICONS.Sparkle size={16} />
        <span className="jev-trigger__label">Perguntar à Lumi</span>
      </button>

      {/* Portal no body: o topo cria um contexto de empilhamento (z-index) que prenderia o painel */}
      {open && createPortal(
        <aside id="jev-panel" className="jev-panel" role="dialog" aria-label="Lumi, assistente de IA da Ingrave">
          <header className="jev-panel__head">
            <div className="jev-panel__title">
              <ICONS.Sparkle size={16} />
              <div className="font-semibold">Lumi</div>
              {page.record && <Badge variant="neutral" className="jev-ctx">nesta tela</Badge>}
            </div>
            <div className="jev-panel__actions">
              <Button variant="ghost" shape="square" icon={ICONS.Plus} aria-label="Nova conversa" onClick={() => { newChat(); setView('chat'); }} />
              <Button variant="ghost" shape="square" icon={ICONS.Clock} aria-label="Conversas anteriores" onClick={() => (view === 'history' ? setView('chat') : openHistory())} />
              <Button variant="ghost" shape="square" icon={ICONS.X} aria-label="Fechar" onClick={closeJev} />
            </div>
          </header>

          {view === 'history' ? (
            <div className="jev-panel__body">
              {threads.length === 0 ? <div className="jev-muted">Nenhuma conversa ainda.</div> : (
                <ul className="jev-threads">
                  {threads.map((t) => (
                    <li key={t.id}>
                      <button type="button" onClick={() => pick(t.id)} className={t.id === threadId ? 'is-active' : ''}>
                        <span>{t.title}</span>
                        <small>{new Date(t.updatedAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</small>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <div className="jev-panel__body" aria-live="polite">
              {items.length === 0 && (
                <div className="jev-empty">
                  <div className="font-semibold">Como posso ajudar?</div>
                  <div className="jev-muted">Consulto e altero dados da organização pelas ferramentas da plataforma. Ações sensíveis sempre pedem sua confirmação.</div>
                  <div className="jev-suggestions">
                    {SUGGESTIONS.map((s) => <button key={s} type="button" onClick={() => send(s)}>{s}</button>)}
                  </div>
                </div>
              )}
              <JevConversation chat={chat} onNavigate={() => { if (window.matchMedia('(max-width: 768px)').matches) closeJev(); }} />
            </div>
          )}

          <JevComposer chat={chat} onSend={(t) => { setView('chat'); send(t); }} />
        </aside>,
        document.body,
      )}
    </>
  );
}
